import { useEffect, useRef, useState } from "react";

/**
 * Per-tick price flash: when the displayed price changes, flash green (up) or
 * red (down), then fade back to the inherited text color (see the
 * `price-flash-*` keyframes in `styles.css`). Generic — currently wired only to
 * the mark price in `use-market-info.tsx`.
 */

type PriceSample = { key: string; value: number; display: string };

/**
 * Decide the flash direction. Pure so it's unit-tested without React/DOM.
 * Fires only when the *displayed* string changes (avoids phantom flashes on
 * sub-precision moves); direction comes from the numeric value. Suppressed on
 * first sample and when the symbol (`key`) changes.
 */
export function flashDirection(
	prev: PriceSample | null,
	curr: PriceSample,
): "up" | "down" | null {
	if (!prev || prev.key !== curr.key) return null;
	if (prev.display === curr.display) return null;
	if (curr.value > prev.value) return "up";
	if (curr.value < prev.value) return "down";
	return null;
}

type PriceFlashProps = {
	/** Numeric price — drives flash direction. */
	value: number;
	/** Formatted string actually rendered. */
	display: string;
	/** Resets tracking (no flash) when it changes — pass the coin/symbol. */
	resetKey: string;
};

export function PriceFlash({ value, display, resetKey }: PriceFlashProps) {
	const prevRef = useRef<PriceSample | null>(null);
	const [flash, setFlash] = useState<{ dir: "up" | "down"; id: number } | null>(
		null,
	);

	useEffect(() => {
		const curr: PriceSample = { key: resetKey, value, display };
		const dir = flashDirection(prevRef.current, curr);
		prevRef.current = curr;
		// Bump `id` to remount the span below, replaying the CSS animation even
		// when the same direction repeats on consecutive ticks.
		if (dir) setFlash((f) => ({ dir, id: (f?.id ?? 0) + 1 }));
	}, [value, display, resetKey]);

	return (
		<span
			key={flash?.id}
			className={
				flash?.dir === "up"
					? "price-flash-up"
					: flash?.dir === "down"
						? "price-flash-down"
						: undefined
			}
		>
			{display}
		</span>
	);
}
